import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'dayjs/locale/en'

// constants
import {
  wholeContainerStyle,
  mainColumnStyle,
  mainContainerStyle,
  listContainerGrayStyle,
  mainContainerHeaderStyle,
  headerBarLeftWrapperStyle,
} from '@/constants/layout'

// components
import { Button } from '@/components/atoms/button'
import { ReloadButton } from '@/components/atoms/reload-button'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { LoadingFull } from '@/components/molecules/loading-full'
import { DetailedSearchPanel } from './detailed-search-panel'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { SalesDetailSearchType, SalesSubjectMasterType } from '@/models/sales-manager/sales'

// apis
import {
  fetchSalesDetailCsv,
  fetchSalesDetailList,
  fetchSalesPayment,
  fetchSalesAccountsReceivable,
  fetchSalesDiscount,
  fetchSalesSubjectMaster,
} from '@/apis/aipass'

// libis
import { csvDownload } from '@/libs/csv-download'
import { useSearchedConditions } from '@/hooks/use-search-condition'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'

// models
import { ApprovedStatusType } from '@/models/reservation-approved-status'

const DISPLAY_LIMIT_IN_PAGE = 30

const reloadWrapperStyle = css({
  marginLeft: 8,
})

type MasterData = {
  subjects: Array<{ id: string; name: string }>
  discounts: Array<{ id: string; name: string }>
  payments: Array<{ id: string; name: string }>
  accountsReceivables: Array<{ id: string; name: string }>
}

export const SalesManagerDetailList: React.FC = () => {
  const searchParams = require('query-string').parse(window.location.search) as Record<string, string | undefined>
  const { t, i18n } = useTranslation()
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()
  const isInitialMount = useRef<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [salesDetailList, setSalesDetailList] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState<number>(Number(searchParams.page) || 1)
  const [totalNumber, setTotalNumber] = useState<number>(0)
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState<boolean>(false)
  const [searchPanelKey, setSearchPanelKey] = useState<number>(0)
  const [masterData, setMasterData] = useState<MasterData>({
    subjects: [],
    discounts: [],
    payments: [],
    accountsReceivables: [],
  })
  const [salesSubjectMasters, setSalesSubjectMasters] = useState<SalesSubjectMasterType[]>([])
  const [detailSearchContent, setDetailSearchContent] = useState<SalesDetailSearchType>({
    beginDate: searchParams.beginDate || undefined,
    endDate: searchParams.endDate || undefined,
    unpaidType: searchParams.unpaidType !== undefined ? (searchParams.unpaidType as '' | '1' | '0') : undefined,
    salesSubjectMasterId: searchParams.salesSubjectMasterId || undefined,
    salesSubSubjectId: searchParams.salesSubSubjectId || undefined,
    salesDiscountId: searchParams.salesDiscountId || undefined,
    discountSubjectName: searchParams.discountSubjectName || undefined,
    minSalesPrice: searchParams.minSalesPrice ? Number(searchParams.minSalesPrice) : undefined,
    maxSalesPrice: searchParams.maxSalesPrice ? Number(searchParams.maxSalesPrice) : undefined,
    salesPaymentId: searchParams.salesPaymentId || undefined,
    paymentMethodName: searchParams.paymentMethodName || undefined,
    salesAccountsReceivableMasterId: searchParams.salesAccountsReceivableMasterId || undefined,
    accountsReceivableName: searchParams.accountsReceivableName || undefined,
    beginPaymentDate: searchParams.beginPaymentDate || undefined,
    beginPaymentHour: searchParams.beginPaymentHour || undefined,
    beginPaymentMinute: searchParams.beginPaymentMinute || undefined,
    endPaymentDate: searchParams.endPaymentDate || undefined,
    endPaymentHour: searchParams.endPaymentHour || undefined,
    endPaymentMinute: searchParams.endPaymentMinute || undefined,
  })
  const history = useHistory()
  const location = useLocation()

  /**
   * 検索条件をURLSearchParamsに変換
   */
  const buildUrlSearchParams = (searchValues: SalesDetailSearchType, page: number): URLSearchParams => {
    const params = new URLSearchParams()
    if (searchValues.beginDate) params.set('beginDate', searchValues.beginDate)
    if (searchValues.endDate) params.set('endDate', searchValues.endDate)
    params.set('page', String(page))

    // 詳細検索パラメータ
    if (searchValues.unpaidType) {
      params.set('unpaidType', searchValues.unpaidType)
    }
    if (searchValues.salesSubjectMasterId) params.set('salesSubjectMasterId', searchValues.salesSubjectMasterId)
    if (searchValues.salesSubSubjectId) params.set('salesSubSubjectId', searchValues.salesSubSubjectId)
    if (searchValues.salesDiscountId) params.set('salesDiscountId', searchValues.salesDiscountId)
    if (searchValues.discountSubjectName) params.set('discountSubjectName', searchValues.discountSubjectName)
    if (searchValues.minSalesPrice !== undefined) params.set('minSalesPrice', String(searchValues.minSalesPrice))
    if (searchValues.maxSalesPrice !== undefined) params.set('maxSalesPrice', String(searchValues.maxSalesPrice))
    if (searchValues.salesPaymentId) params.set('salesPaymentId', searchValues.salesPaymentId)
    if (searchValues.paymentMethodName) params.set('paymentMethodName', searchValues.paymentMethodName)
    if (searchValues.salesAccountsReceivableMasterId)
      params.set('salesAccountsReceivableMasterId', searchValues.salesAccountsReceivableMasterId)
    if (searchValues.accountsReceivableName) params.set('accountsReceivableName', searchValues.accountsReceivableName)
    if (searchValues.beginPaymentDate) {
      params.set('beginPaymentDate', searchValues.beginPaymentDate)
      if (searchValues.beginPaymentHour) params.set('beginPaymentHour', searchValues.beginPaymentHour)
      if (searchValues.beginPaymentMinute) params.set('beginPaymentMinute', searchValues.beginPaymentMinute)
    }
    if (searchValues.endPaymentDate) {
      params.set('endPaymentDate', searchValues.endPaymentDate)
      if (searchValues.endPaymentHour) params.set('endPaymentHour', searchValues.endPaymentHour)
      if (searchValues.endPaymentMinute) params.set('endPaymentMinute', searchValues.endPaymentMinute)
    }

    return params
  }

  const onClearSearchPanel = () => {
    // 利用明細日付以外は全てクリア
    setDetailSearchContent({
      beginDate: detailSearchContent.beginDate,
      endDate: detailSearchContent.endDate,
      unpaidType: undefined,
      salesSubjectMasterId: undefined,
      salesSubSubjectId: undefined,
      salesDiscountId: undefined,
      discountSubjectName: undefined,
      minSalesPrice: undefined,
      maxSalesPrice: undefined,
      salesPaymentId: undefined,
      paymentMethodName: undefined,
      salesAccountsReceivableMasterId: undefined,
      accountsReceivableName: undefined,
      beginPaymentDate: undefined,
      beginPaymentHour: undefined,
      beginPaymentMinute: undefined,
      endPaymentDate: undefined,
      endPaymentHour: undefined,
      endPaymentMinute: undefined,
    })
  }

  // 検索処理を1箇所に集約
  const executeSearch = async (searchValues: SalesDetailSearchType, page: number) => {
    setIsLoading(true)
    try {
      const res = await fetchSalesDetailList(
        searchValues.beginDate || '',
        searchValues.endDate || '',
        page,
        DISPLAY_LIMIT_IN_PAGE,
        searchValues,
      )

      if (res) {
        setTotalNumber(res.total || 0)
        setSalesDetailList(res.sales || [])
        scrollListToTop()
      } else {
        setTotalNumber(0)
        setSalesDetailList([])
      }
    } catch (error) {
      console.error('Failed to fetch sales detail list:', error)
      setTotalNumber(0)
      setSalesDetailList([])
    } finally {
      setIsLoading(false)
    }
  }

  const doSearchSalesDetail = async () => {
    setIsSearchPanelOpen(false)
    await executeSearch(detailSearchContent, currentPage)
    const params = buildUrlSearchParams(detailSearchContent, currentPage)
    history.replace({
      pathname: '',
      search: params.toString(),
    })
  }

  const onDetailSearch = async (searchValues: SalesDetailSearchType) => {
    setIsSearchPanelOpen(false)
    setDetailSearchContent(searchValues)
    setCurrentPage(1)

    // 検索を直接実行（顧客管理と同じ挙動）
    await executeSearch(searchValues, 1)
    const params = buildUrlSearchParams(searchValues, 1)
    history.replace({
      pathname: '',
      search: params.toString(),
    })
  }

  // csv download
  const downloadSalesCsv = async () => {
    setIsLoading(true)
    try {
      const res = await fetchSalesDetailCsv(detailSearchContent.beginDate || '', detailSearchContent.endDate || '', detailSearchContent)
      const data = res?.salesDetailListCsv
      const fileName = 'sales-detail-list.csv'
      csvDownload(data, fileName)
    } catch (error) {
      console.error('CSV download failed:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const getAccommodationDetailUrl = (checkinId: string, approvedStatus: ApprovedStatusType): string => {
    let accommodationDetailUrl = '#'
    if (approvedStatus === ApprovedStatusType.Checkin) {
      accommodationDetailUrl = `/checkin/${checkinId}?tabType=checkin`
    }
    if (approvedStatus === ApprovedStatusType.Stay) {
      accommodationDetailUrl = `/stay/${checkinId}?tabType=stay`
    }
    if (approvedStatus === ApprovedStatusType.Checkout) {
      accommodationDetailUrl = `/checkout/${checkinId}?tabType=checkout`
    }
    return accommodationDetailUrl
  }

  useEffect(() => {
    // マスタデータ取得
    const convertToIdNameArray = <T extends { id: string; name?: string }>(items: T[]): Array<{ id: string; name: string }> => {
      return items.map(item => ({ id: item.id, name: item.name || '' }))
    }

    Promise.all([
      fetchSalesSubjectMaster().catch(e => {
        console.error('Failed to fetch sales subject master:', e)
        return [] as SalesSubjectMasterType[]
      }),
      fetchSalesDiscount().catch(e => {
        console.error('Failed to fetch sales discount:', e)
        return undefined
      }),
      fetchSalesPayment().catch(e => {
        console.error('Failed to fetch sales payment:', e)
        return undefined
      }),
      fetchSalesAccountsReceivable().catch(e => {
        console.error('Failed to fetch sales accounts receivable:', e)
        return undefined
      }),
    ]).then(([subjectRes, discountRes, paymentRes, accountsRes]) => {
      const subjects: Array<{ id: string; name: string }> = []
      ;(subjectRes || []).forEach(s => {
        subjects.push({ id: s.id, name: s.subjectName })
        ;(s.subSubjects || []).forEach(ss => subjects.push({ id: ss.id, name: `${s.subjectName} ${ss.subSubjectName}` }))
      })
      setSalesSubjectMasters(subjectRes || [])
      setMasterData({
        subjects,
        discounts: convertToIdNameArray(discountRes?.salesDiscount || []),
        payments: convertToIdNameArray(paymentRes?.salesPayment || []),
        accountsReceivables: convertToIdNameArray(accountsRes?.accountsReceivables || []),
      })
    })
  }, [])

  // ページが変わったときに検索を実行（顧客管理と同じ挙動）
  useEffect(() => {
    // 初期マウント時はスキップ（初期表示時のuseEffectで処理する）
    if (isInitialMount.current) {
      return
    }
    // 日付範囲のチェック（beginDateとendDateの両方がある、または両方ない場合のみ検索）
    const hasDateRange = !!detailSearchContent.beginDate && !!detailSearchContent.endDate
    const hasNoDateRange = !detailSearchContent.beginDate && !detailSearchContent.endDate
    if (hasDateRange || hasNoDateRange) {
      doSearchSalesDetail()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  // 初期表示時、URLパラメータがある場合は検索を実行
  useEffect(() => {
    const initializeSearch = async () => {
      // location.searchから直接パラメータを読み取る
      const currentSearchParams = require('query-string').parse(location.search) as Record<string, string | undefined>

      // URLパラメータがある場合のみ初期検索を実行
      const hasUrlParams = Object.keys(currentSearchParams).length > 0

      if (hasUrlParams) {
        // detailSearchContentを更新
        const updatedSearchContent: SalesDetailSearchType = {
          beginDate: currentSearchParams.beginDate || undefined,
          endDate: currentSearchParams.endDate || undefined,
          unpaidType: currentSearchParams.unpaidType !== undefined ? (currentSearchParams.unpaidType as '' | '1' | '0') : undefined,
          salesSubjectMasterId: currentSearchParams.salesSubjectMasterId || undefined,
          salesSubSubjectId: currentSearchParams.salesSubSubjectId || undefined,
          salesDiscountId: currentSearchParams.salesDiscountId || undefined,
          discountSubjectName: currentSearchParams.discountSubjectName || undefined,
          minSalesPrice: currentSearchParams.minSalesPrice ? Number(currentSearchParams.minSalesPrice) : undefined,
          maxSalesPrice: currentSearchParams.maxSalesPrice ? Number(currentSearchParams.maxSalesPrice) : undefined,
          salesPaymentId: currentSearchParams.salesPaymentId || undefined,
          paymentMethodName: currentSearchParams.paymentMethodName || undefined,
          salesAccountsReceivableMasterId: currentSearchParams.salesAccountsReceivableMasterId || undefined,
          accountsReceivableName: currentSearchParams.accountsReceivableName || undefined,
          beginPaymentDate: currentSearchParams.beginPaymentDate || undefined,
          beginPaymentHour: currentSearchParams.beginPaymentHour || undefined,
          beginPaymentMinute: currentSearchParams.beginPaymentMinute || undefined,
          endPaymentDate: currentSearchParams.endPaymentDate || undefined,
          endPaymentHour: currentSearchParams.endPaymentHour || undefined,
          endPaymentMinute: currentSearchParams.endPaymentMinute || undefined,
        }

        setDetailSearchContent(updatedSearchContent)
        const initialPage = Number(currentSearchParams.page) || 1
        setCurrentPage(initialPage)

        // 日付範囲のチェック（beginDateとendDateの両方がある、または両方ない場合のみ検索）
        const hasDateRange = !!updatedSearchContent.beginDate && !!updatedSearchContent.endDate
        const hasNoDateRange = !updatedSearchContent.beginDate && !updatedSearchContent.endDate

        if (hasDateRange || hasNoDateRange) {
          // 検索を直接実行（URLパラメータから遷移してきた場合）
          isInitialMount.current = false
          await executeSearch(updatedSearchContent, initialPage)
        } else {
          isInitialMount.current = false
        }
      } else {
        // URLパラメータがない場合でも、初期表示時に検索を実行（顧客管理と同じ挙動）
        // ただし、日付範囲が設定されている場合のみ
        const hasDateRange = !!detailSearchContent.beginDate && !!detailSearchContent.endDate
        const hasNoDateRange = !detailSearchContent.beginDate && !detailSearchContent.endDate

        if (hasDateRange || hasNoDateRange) {
          isInitialMount.current = false
          await executeSearch(detailSearchContent, currentPage)
          const params = buildUrlSearchParams(detailSearchContent, currentPage)
          history.replace({
            pathname: '',
            search: params.toString(),
          })
        } else {
          isInitialMount.current = false
        }
      }
    }

    initializeSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nameDomBySales = (salesDetail: {
    checkinId?: string
    approvedStatus?: ApprovedStatusType
    reservationGuestName?: string
    isCompany?: boolean
    salesDate?: string
    pmsTransactionId?: string
    salesManagerDailyId?: string
  }) => {
    if (salesDetail.checkinId && salesDetail.approvedStatus) {
      return (
        <a href={getAccommodationDetailUrl(salesDetail.checkinId, salesDetail.approvedStatus)}>
          <p css={nameStyle}>{salesDetail.reservationGuestName || '-'}</p>
        </a>
      )
    } else if (salesDetail.isCompany) {
      return (
        <a href={`/sales-manager/advance-money?companyName=${salesDetail.reservationGuestName}&dateTime=${salesDetail.salesDate}`}>
          <p css={nameStyle} style={{ width: '100%' }}>
            {salesDetail.reservationGuestName || '-'}
          </p>
        </a>
      )
    } else if (salesDetail.pmsTransactionId && salesDetail.salesManagerDailyId) {
      return (
        <a
          href={`/sales-manager/detail-day?id=${salesDetail.salesManagerDailyId}&transactionId=${salesDetail.pmsTransactionId}`}
          style={{ width: 'fit-content' }}
        >
          -
        </a>
      )
    } else {
      return '-'
    }
  }

  const formatSubjectDisplayName = (salesDetail: { salesSubjectName?: string; salesSubSubjectName?: string }) => {
    // 科目自体がない場合は空文字を返却
    if (!salesDetail?.salesSubjectName) return ''
    // 科目・補助科目がある場合は、半角スペースでつなげて返却
    if (salesDetail?.salesSubSubjectName) {
      return `${salesDetail.salesSubjectName} ${salesDetail.salesSubSubjectName}`
    }
    // 科目のみの場合は、科目名をそのまま返却
    return salesDetail.salesSubjectName
  }

  // 検索条件の表示用データ
  const getSearchedConditions = () => {
    const conditions: Record<string, string[]> = {}
    if (detailSearchContent.beginDate || detailSearchContent.endDate) {
      const begin = detailSearchContent.beginDate ? dayjs(detailSearchContent.beginDate).format('YYYY年MM月DD日') : ''
      const end = detailSearchContent.endDate ? dayjs(detailSearchContent.endDate).format('YYYY年MM月DD日') : ''
      conditions[t('Sales date')] = [begin && end ? `${begin} ~ ${end}` : begin || end]
    }
    if (detailSearchContent.unpaidType) {
      conditions[t('Amount to be collected')] = [detailSearchContent.unpaidType === '1' ? t('Exist') : t('None')]
    }
    const subjectOrSubId = detailSearchContent.salesSubSubjectId || detailSearchContent.salesSubjectMasterId
    if (subjectOrSubId) {
      const subject = masterData.subjects.find(s => s.id === subjectOrSubId)
      if (subject?.name) {
        conditions[t('SubjectAndSubSubject')] = [subject.name]
      }
    }
    if (detailSearchContent.salesDiscountId) {
      const name = masterData.discounts.find(d => d.id === detailSearchContent.salesDiscountId)?.name
      conditions[t('Discount subject')] = [name || detailSearchContent.salesDiscountId]
    }
    if (detailSearchContent.minSalesPrice !== undefined || detailSearchContent.maxSalesPrice !== undefined) {
      const min = detailSearchContent.minSalesPrice || 0
      const max = detailSearchContent.maxSalesPrice || ''
      conditions[t('Earnings')] = [`${min} ~ ${max}`]
    }
    if (detailSearchContent.paymentMethodName) {
      conditions[t('Method of payment')] = [detailSearchContent.paymentMethodName]
    } else if (detailSearchContent.salesPaymentId) {
      const name = masterData.payments.find(p => p.id === detailSearchContent.salesPaymentId)?.name
      conditions[t('Method of payment')] = [name || detailSearchContent.salesPaymentId]
    }
    if (detailSearchContent.accountsReceivableName) {
      conditions[t('Accounts receivable')] = [detailSearchContent.accountsReceivableName]
    } else if (detailSearchContent.salesAccountsReceivableMasterId) {
      const name = masterData.accountsReceivables.find(a => a.id === detailSearchContent.salesAccountsReceivableMasterId)?.name
      conditions[t('Accounts receivable')] = [name || detailSearchContent.salesAccountsReceivableMasterId]
    }
    if (detailSearchContent.beginPaymentDate || detailSearchContent.endPaymentDate) {
      const beginTime =
        detailSearchContent.beginPaymentHour && detailSearchContent.beginPaymentMinute
          ? `${detailSearchContent.beginPaymentHour}:${detailSearchContent.beginPaymentMinute}`
          : '00:00'
      const endTime =
        detailSearchContent.endPaymentHour && detailSearchContent.endPaymentMinute
          ? `${detailSearchContent.endPaymentHour}:${detailSearchContent.endPaymentMinute}`
          : '23:59'
      const begin = detailSearchContent.beginPaymentDate
        ? `${dayjs(detailSearchContent.beginPaymentDate).format('YYYY年MM月DD日')} ${beginTime}`
        : ''
      const end = detailSearchContent.endPaymentDate
        ? `${dayjs(detailSearchContent.endPaymentDate).format('YYYY年MM月DD日')} ${endTime}`
        : ''
      conditions[t('Payment Date')] = [begin && end ? `${begin} ~ ${end}` : begin || end]
    }
    return conditions
  }

  const searchedConditions = React.useMemo(() => getSearchedConditions(), [detailSearchContent, masterData])
  const { SearchedConditionValues } = useSearchedConditions(searchedConditions)

  return (
    <div css={listContainerGrayStyle}>
      <div css={wholeContainerStyle}>
        <SideMenu />
        <div className="mainColumn" css={mainColumnStyle}>
          <Header isBack title={t('SaleDetail')} goBack={() => history.goBack()} />
          <div css={[mainContainerStyle, { position: 'relative' }]}>
            <div css={{ borderRadius: 5, boxShadow: '0px 0px 10px #00000029', overflow: 'hidden' }}>
              <div css={[mainContainerHeaderStyle, { minWidth: 'auto' }]}>
                <div css={[headerBarLeftWrapperStyle, { flex: 1, minWidth: 0, marginRight: 16 }]}>
                  <Button
                    buttonType={4}
                    width={i18n.language === 'en' ? 118 : 90}
                    height={32}
                    fontSize={12}
                    marginLeft={8}
                    marginRight={8}
                    onClick={() => {
                      if (!isSearchPanelOpen) {
                        setSearchPanelKey(prev => prev + 1) // 開く時に再マウント
                      }
                      setIsSearchPanelOpen(!isSearchPanelOpen)
                    }}
                  >
                    {t('Detailed search')}
                  </Button>
                  <div css={reloadWrapperStyle}>
                    <ReloadButton onClick={() => executeSearch(detailSearchContent, currentPage)} />
                  </div>
                  {!isSearchPanelOpen && <SearchedConditionValues />}
                </div>
                <div css={[headerBarLeftWrapperStyle, { flexShrink: 0 }]}>
                  <Button
                    buttonType={4}
                    width={i18n.language === 'en' ? 160 : 140}
                    height={32}
                    fontSize={12}
                    icon={require('@/static/images/csv.svg')}
                    onClick={downloadSalesCsv}
                  >
                    {t('CSV download')}
                  </Button>
                  <AccommodationPaging
                    page={currentPage}
                    setPage={handlePageChange}
                    total={totalNumber || 0}
                    limit={DISPLAY_LIMIT_IN_PAGE}
                  />
                </div>
              </div>
              <div ref={listScrollRef} css={{ overflow: 'auto', maxHeight: '78vh' }}>
                <div css={detailListContainerStyle}>
                  <div css={detailListHeaderContainerStyle}>
                    <ul css={detailListLabelContainerStyle}>
                      <li css={dateLabelStyle}>{t('Date')}</li>
                      <li css={nameLabelStyle}>{t('Guest')}</li>
                      <li css={reservationIdLabelStyle}>{t('Reservation ID')}</li>
                      <li css={subjectsLabelStyle}>{t('SubjectAndSubSubject')}</li>
                      <li css={unitPriceLabelStyle}>{t('Unit price')}</li>
                      <li css={quantityLabelStyle}>{t('Quantity')}</li>
                      <li css={salesLabelStyle}>{t('Earnings')}</li>
                      <li css={paymentMethodsLabelStyle}>{t('Method of payment')}</li>
                      <li css={accountsReceivableLabelStyle}>{t('Accounts receivable')}</li>
                      <li css={paymentLabelStyle}>{t('Payment')}</li>
                      <li css={unpaidMoneyLabelStyle}>{t('Amount to be collected')}</li>
                      <li css={paymentDateLabelStyle}>{t('Payment Date')}</li>
                    </ul>
                  </div>
                  <div css={detailListMainContainerStyle}>
                    {salesDetailList.length > 0 &&
                      salesDetailList?.map((salesDetail, index) => (
                        <div key={index}>
                          <ul css={!salesDetail.isConfirm ? detailListItemContainerStyle : confirmedDetailListItemContainerStyle}>
                            {salesDetail.salesSubjectId !== null || salesDetail.isCompany ? (
                              <>
                                <li css={dateStyle}>{dayjs(salesDetail.salesDate).format(t('MM-DD'))}</li>
                                <li css={nameWrapperStyle}>{nameDomBySales(salesDetail)}</li>
                                <li css={reservationIdStyle}>{salesDetail?.reservationId ? salesDetail?.reservationId : '-'}</li>
                                <li css={subjectsStyle}>{formatSubjectDisplayName(salesDetail)}</li>
                                <li css={unitPriceStyle}>
                                  {salesDetail?.salesSubjectPrice ? salesDetail?.salesSubjectPrice.toLocaleString() : 0}
                                </li>
                                <li css={quantityStyle}>{salesDetail?.quantity ? salesDetail?.quantity : 0}</li>
                                <li css={salesStyle}>{salesDetail?.salesPrice ? salesDetail?.salesPrice.toLocaleString() : 0}</li>
                                <li css={paymentMethodsStyle}>{salesDetail?.salesPaymentName ? salesDetail?.salesPaymentName : ''}</li>
                                <li css={accountsReceivableStyle}>{salesDetail?.accountsReceivableName || '-'}</li>
                                <li css={paymentStyle}>{salesDetail?.price ? salesDetail?.price.toLocaleString() : 0}</li>
                                {/* 未収金と繰越金の額が一致していない場合は、未収金を目立たせる */}
                                <li css={salesDetail?.unpaid !== salesDetail?.carriedForward ? isUnpaidStyle : unpaidMoneyStyle}>
                                  {salesDetail?.unpaid ? salesDetail?.unpaid.toLocaleString() : 0}
                                </li>
                                <li css={paymentDateStyle}>
                                  {salesDetail?.paymentDate ? dayjs(salesDetail.paymentDate).format(t('MM-DD HH:mm')) : '-'}
                                </li>
                              </>
                            ) : salesDetail.isPaymentAllocation ? (
                              <>
                                <li css={dateStyle}>{dayjs(salesDetail.salesDate).format(t('MM-DD'))}</li>
                                <li css={nameWrapperStyle}>{nameDomBySales(salesDetail)}</li>
                                <li css={reservationIdStyle}>{salesDetail?.reservationId ? salesDetail?.reservationId : '-'}</li>
                                <li css={subjectsStyle}>{salesDetail?.salesDiscountName ? salesDetail?.salesDiscountName : ''}</li>
                                <li css={unitPriceStyle}>{'-'}</li>
                                <li css={quantityStyle}>{'-'}</li>
                                <li css={salesStyle}>{'-'}</li>
                                <li css={paymentMethodsStyle}>{salesDetail?.salesPaymentName ? salesDetail?.salesPaymentName : ''}</li>
                                <li css={accountsReceivableStyle}>{salesDetail?.accountsReceivableName || '-'}</li>
                                <li css={paymentStyle}>
                                  {salesDetail?.salesSubjectPrice > 0 ? salesDetail?.salesSubjectPrice.toLocaleString() : 0}
                                </li>
                                <li css={unpaidMoneyStyle}>{'-'}</li>
                                <li css={paymentDateStyle}>
                                  {salesDetail?.paymentDate ? dayjs(salesDetail.paymentDate).format(t('MM-DD HH:mm')) : '-'}
                                </li>
                              </>
                            ) : (
                              <>
                                <li css={dateStyle}>{dayjs(salesDetail.salesDate).format(t('MM-DD'))}</li>
                                <li css={nameWrapperStyle}>{nameDomBySales(salesDetail)}</li>
                                <li css={reservationIdStyle}>{salesDetail.reservationId ? salesDetail.reservationId : '-'}</li>
                                <li css={subjectsStyle}>{salesDetail.salesDiscountName ? salesDetail.salesDiscountName : ''}</li>
                                <li css={unitPriceStyle}>{(salesDetail.salesSubjectPrice * -1).toLocaleString()}</li>
                                <li css={quantityStyle}>1</li>
                                <li css={salesStyle}>{(salesDetail.salesSubjectPrice * -1).toLocaleString()}</li>
                                <li css={paymentMethodsStyle}>{salesDetail.salesPaymentName ? salesDetail.salesPaymentName : ''}</li>
                                <li css={accountsReceivableStyle}>{salesDetail.accountsReceivableName || '-'}</li>
                                <li css={paymentStyle}>
                                  {salesDetail.carriedForward ? 0 : (salesDetail.salesSubjectPrice * -1).toLocaleString()}
                                </li>
                                <li css={unpaidMoneyStyle}>
                                  {salesDetail.carriedForward ? salesDetail?.carriedForward.toLocaleString() : '-'}
                                </li>
                                <li css={paymentDateStyle}>
                                  {salesDetail.paymentDate ? dayjs(salesDetail.paymentDate).format(t('MM-DD HH:mm')) : '-'}
                                </li>
                              </>
                            )}
                          </ul>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            {isSearchPanelOpen && (
              <DetailedSearchPanel
                key={`search-panel-${searchPanelKey}`}
                searchContent={detailSearchContent}
                onClear={onClearSearchPanel}
                onSearch={searchValues => {
                  onDetailSearch(searchValues)
                  setIsSearchPanelOpen(false)
                  setSearchPanelKey(prev => prev + 1) // 次回開く時に再マウント
                }}
                salesSubjectMasters={salesSubjectMasters}
                salesPaymentOptions={masterData.payments}
                salesAccountsReceivableOptions={masterData.accountsReceivables}
                salesDiscountOptions={masterData.discounts}
              />
            )}
          </div>
          <LoadingFull isLoading={isLoading} />
        </div>
      </div>
    </div>
  )
}

const detailListContainerStyle = css({
  backgroundColor: '#FFF',
  width: '100%',
})

const detailListHeaderContainerStyle = css({
  position: 'sticky',
  top: 0,
})

const detailListLabelContainerStyle = css({
  display: 'flex',
  backgroundColor: '#F5F5F5',
  height: 32,
  fontSize: 12,
  color: '#676767',
  padding: '10px 10px 10px 32px',
  fontWeight: 'bold',
})

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const dateLabelStyle = css({
  width: '7%',
  marginRight: '2%',
})

const dateStyle = css(dateLabelStyle, {
  whiteSpace: 'nowrap',
})

const paymentDateLabelStyle = css({
  width: '13%',
  marginRight: '2%',
})

const paymentDateStyle = css(paymentDateLabelStyle, {
  whiteSpace: 'nowrap',
})

const nameLabelStyle = css({
  width: '10%',
  marginRight: '2%',
  minWidth: 80,
})

const nameWrapperStyle = css(nameLabelStyle, {
  color: '#F2A40B',
  '> a': {
    color: '#F2A40B',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
})

const nameStyle = css(ellipsisStyle, {
  width: '80%',
  lineHeight: '14px',
})

const subjectsLabelStyle = css({
  width: '12%',
  marginRight: '2%',
})

const accountsReceivableLabelStyle = css(subjectsLabelStyle, {
  width: '8%',
})

const reservationIdLabelStyle = css(subjectsLabelStyle, {
  minWidth: 90,
  maxWidth: 90,
})

const subjectsStyle = css(subjectsLabelStyle, ellipsisStyle)

const accountsReceivableStyle = css(subjectsLabelStyle, ellipsisStyle, {
  width: '8%',
})

const reservationIdStyle = css(subjectsLabelStyle, ellipsisStyle, {
  minWidth: 90,
  maxWidth: 90,
})

const unitPriceLabelStyle = css({
  width: '6%',
  marginRight: '2%',
})

const unitPriceStyle = css(unitPriceLabelStyle, ellipsisStyle)

const quantityLabelStyle = css({
  width: '4%',
  marginRight: '2%',
})

const quantityStyle = css(quantityLabelStyle)

const salesLabelStyle = css({
  width: '6%',
  marginRight: '2%',
})

const salesStyle = css(salesLabelStyle)

const paymentMethodsLabelStyle = css({
  width: '12%',
  marginRight: '2%',
})

const paymentMethodsStyle = css(paymentMethodsLabelStyle, ellipsisStyle)

const paymentLabelStyle = css({
  width: '8%',
  marginRight: '2%',
})

const paymentStyle = css(paymentLabelStyle)

const unpaidMoneyLabelStyle = css({
  width: '7%',
  marginRight: '1%',
  whiteSpace: 'nowrap',
})

const unpaidMoneyStyle = css(unpaidMoneyLabelStyle, {
  color: '#272727',
})

const isUnpaidStyle = css(unpaidMoneyLabelStyle, {
  color: '#F2A40B',
})

const detailListMainContainerStyle = css({
  width: '100%',
})

const detailListItemContainerStyle = css({
  display: 'flex',
  color: '#272727',
  fontSize: 14,
  height: 46,
  padding: '16px 10px 16px 32px',
  borderBottom: '1px solid #F2F2F2',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    cursor: 'default',
  },
})

const confirmedDetailListItemContainerStyle = css(detailListItemContainerStyle, {
  background: '#f5f5f5',
  '&:hover': {
    boxShadow: 'none',
  },
})
